import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { firstValueFrom } from 'rxjs';
import { SfoDynamicFormModule } from '../form-builder/dynamic-form.module';
import { SfoUiJSONSchema7 } from '../metadata.model';
import { SfoAdmonitionComponent } from '../widgets/admonition/admonition.component';
import {
  FieldComparisonHelper,
  SchemaChangeConfirmationDialogComponent,
  SchemaChangeDialogData,
} from './schema-change-dialog';
import { RenderMode } from '../form-builder/types/view.types';
import { normalizeCslPatchType } from './helpers/normalize-csl-patch.helper';

interface SchemaSelectionResult {
  schema: SfoUiJSONSchema7 | null;
  selectorValue: string | null;
}

interface SchemaSelectorOption {
  value: string;
  label: string;
  _ui_disable?: boolean;
}

/**
 * CSL Item Type selector metadata
 * Extracted from definitions.schema.json for hardcoded configuration
 */
const CSL_SELECTOR_LABEL = 'Type';
const CSL_SELECTOR_DESCRIPTION =
  'Select a reference (bibliography) type for this item (e.g., Journal Article, Book, Web Page).';

const CSL_SELECTOR_OPTIONS: SchemaSelectorOption[] = [
  { value: 'article', label: 'Article' },
  { value: 'article-journal', label: 'Journal Article' },
  { value: 'article-magazine', label: 'Magazine Article' },
  { value: 'article-newspaper', label: 'Newspaper Article' },
  { value: 'bill', label: 'Bill' },
  { value: 'book', label: 'Book' },
  { value: 'broadcast', label: 'Broadcast' },
  { value: 'chapter', label: 'Book Chapter' },
  { value: 'classic', label: 'Classic' },
  { value: 'collection', label: 'Collection' },
  { value: 'dataset', label: 'Dataset' },
  { value: 'document', label: 'Document' },
  { value: 'entry', label: 'Entry' },
  { value: 'entry-dictionary', label: 'Dictionary Entry' },
  { value: 'entry-encyclopedia', label: 'Encyclopedia Entry' },
  { value: 'event', label: 'Event' },
  { value: 'figure', label: 'Figure' },
  { value: 'graphic', label: 'Graphic' },
  { value: 'hearing', label: 'Hearing' },
  { value: 'interview', label: 'Interview' },
  { value: 'legal_case', label: 'Legal Case' },
  { value: 'legislation', label: 'Legislation' },
  { value: 'manuscript', label: 'Manuscript' },
  { value: 'map', label: 'Map' },
  { value: 'motion_picture', label: 'Motion Picture' },
  { value: 'musical_score', label: 'Musical Score' },
  { value: 'pamphlet', label: 'Pamphlet' },
  { value: 'paper-conference', label: 'Conference Paper' },
  { value: 'patent', label: 'Patent' },
  { value: 'performance', label: 'Performance' },
  { value: 'periodical', label: 'Periodical' },
  { value: 'personal_communication', label: 'Personal Communication' },
  { value: 'post', label: 'Post' },
  { value: 'post-weblog', label: 'Blog Post' },
  { value: 'regulation', label: 'Regulation' },
  { value: 'report', label: 'Report' },
  { value: 'review', label: 'Review' },
  { value: 'review-book', label: 'Book Review' },
  { value: 'software', label: 'Software' },
  { value: 'song', label: 'Song' },
  { value: 'speech', label: 'Speech' },
  { value: 'standard', label: 'Standard' },
  { value: 'thesis', label: 'Thesis' },
  { value: 'treaty', label: 'Treaty' },
  { value: 'webpage', label: 'Web Page' },
];

/**
 * CSL Schema Selector Component
 *
 * Manages multiple CSL schemas with a dropdown selector for switching between reference types.
 * Handles schema selection, data loss detection, and confirmation dialogs.
 * Wraps the DynamicFormComponent and passes it the active schema.
 *
 * @example
 * ```html
 * <sfo-csl-schema-selector
 *   [cslSchemas]="resolvedSchemas"
 *   [patchForm]="referenceData"
 *   (formValue)="onFormChange($event)">
 * </sfo-csl-schema-selector>
 * ```
 */
@Component({
  selector: 'sfo-csl-schema-selector',
  templateUrl: './csl-schema-selector.component.html',
  styleUrls: ['./csl-schema-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatOptionModule,
    MatTooltipModule,
    MatDialogModule,
    SfoDynamicFormModule,
    SfoAdmonitionComponent,
  ],
})
export class CslSchemaSelectorComponent {
  private dialog = inject(MatDialog);

  // Form controls
  schemaSelectorControl = new FormControl<string | null>(null);

  /**
   * Array of CSL schemas (journal-article, webpage, etc.).
   * Each schema must have a `properties.type.const` value matching a CSL item type.
   * No metadata (definitions, _ui_schema_selector) is required in the schemas.
   */
  cslSchemas = input<SfoUiJSONSchema7[]>([]);

  /**
   * Patch data to apply to the form.
   * If includes a `type` field, automatically selects the corresponding schema,
   * unless user has manually selected a schema (which locks the selection).
   */
  patchForm = input<object | null>(null);
  renderMode: RenderMode = 'simple';

  /**
   * Emits the current form value when valid and dirty.
   * Called when the form value changes and is valid.
   */
  formValue = output<object>();

  /**
   * Emits when patch validation fails.
   * Called when patch data cannot be applied to the form.
   */
  patchError = output<unknown | unknown[]>();

  // Internal state signals
  private isUserDriven = signal<boolean>(false);
  private userSelectedType = signal<string | null>(null);
  formErrors = signal<string | null>(null);
  schemaErrors = signal<string | null>(null);
  normalizedPatch = computed<object | null>(() => {
    return normalizeCslPatchType(this.patchForm());
  });

  /**
   * True if multiple schemas are provided (schema selector should be shown).
   */
  isSchemaSelector = computed(() => {
    return this.cslSchemas().length > 0;
  });

  /**
   * Options for the schema selector dropdown (hardcoded CSL item types).
   */
  schemaSelectorOptions = computed<SchemaSelectorOption[]>(() => {
    return this.cslSchemas().length > 0 ? CSL_SELECTOR_OPTIONS : [];
  });

  /**
   * Gets the label for the schema selector dropdown.
   */
  schemaSelectorLabel = computed<string>(() => {
    return this.cslSchemas().length > 0 ? CSL_SELECTOR_LABEL : 'Select an option';
  });

  /**
   * Gets the description for the schema selector dropdown.
   */
  schemaSelectorDescription = computed<string | null>(() => {
    return this.cslSchemas().length > 0 ? CSL_SELECTOR_DESCRIPTION : null;
  });

  /**
   * Determines which schema type to use based on patch data or user selection.
   *
   * Two modes with a one-way lock:
   *
   * 1. Patch-driven (automatic):
   *    - Patch data with `type` field determines schema
   *    - Happens when isUserDriven = false
   *
   * 2. User-driven (manual):
   *    - User selects from dropdown
   *    - Sets isUserDriven = true (LOCKS the mode)
   *    - Subsequent patches CANNOT change the schema
   *
   * Reset: isUserDriven resets to false when jsonSchema or patch type changes
   */
  private schemaTypeSelection = computed<string | null>(() => {
    const patch = this.normalizedPatch();
    const isUserDriven = this.isUserDriven();

    // Mode 1: Automatic selection from patch data (only if not user-driven)
    if (!isUserDriven && patch && typeof patch === 'object' && 'type' in patch) {
      const patchType = (patch as Record<string, unknown>).type;
      if (typeof patchType === 'string') {
        return patchType;
      }
    }

    // Mode 2: User-driven selection takes priority
    if (isUserDriven) {
      return this.userSelectedType();
    }

    return null;
  });

  /**
   * Resolves the active schema based on the selected type.
   */
  private activeSchemaSelection = computed<SchemaSelectionResult>(() => {
    const schemas = this.cslSchemas();
    const selectedType = this.schemaTypeSelection();

    if (schemas.length === 0 || !selectedType) {
      return { schema: null, selectorValue: null };
    }

    // Find matching schema from cslSchemas by type.const value
    const matchingSchema = schemas.find((schema) => {
      return schema.properties?.type?.const === selectedType;
    });

    return {
      schema: matchingSchema || null,
      selectorValue: selectedType,
    };
  });

  /**
   * The currently active schema to pass to dynamic-form.
   */
  activeSchema = computed<SfoUiJSONSchema7 | null>(() => this.activeSchemaSelection().schema);

  /**
   * The currently selected schema type value.
   */
  previousSelectedValue = computed<string | null>(() => this.activeSchemaSelection().selectorValue);

  /**
   * Tracks whether the form is ready for rendering.
   */
  formReady = computed<boolean>(() => !!this.activeSchema());

  /**
   * Transforms the patch data to match the active schema.
   * Filters out fields that don't exist in the target schema and updates the 'type' field
   * when user manually switches schemas to prevent validation errors.
   */
  transformedPatch = computed<object | null>(() => {
    const patch = this.normalizedPatch();
    const selectedType = this.schemaTypeSelection();
    const isUserDriven = this.isUserDriven();
    const activeSchema = this.activeSchema();

    if (!patch || !selectedType) {
      return patch;
    }

    // Filter patch to only include fields valid for the active schema
    // This prevents validation errors when fields from one schema don't exist in another
    const filteredPatch = this.filterPatchForSchema(patch, activeSchema);

    // In user-driven mode, update the type field to match the selected schema
    // This prevents validation errors when switching from one schema to another
    if (isUserDriven && filteredPatch) {
      return {
        ...filteredPatch,
        type: selectedType,
      };
    }

    // In patch-driven mode, pass filtered patch as-is (type should already match)
    return filteredPatch;
  });

  constructor() {
    // Effect 0: Reset user-driven flag when cslSchemas or patchForm type changes
    effect(() => {
      // Track dependencies
      const schemas = this.cslSchemas();
      const patch = this.normalizedPatch();
      console.log('CslSchemaSelectorComponent effect triggered', { schemas, patch });

      // Check if patch has a type field
      const hasTypeField =
        !!patch &&
        typeof patch === 'object' &&
        'type' in patch &&
        typeof (patch as Record<string, unknown>).type === 'string';

      // Reset user-driven flag when schemas or patch type changes
      if (hasTypeField || schemas.length > 0) {
        this.isUserDriven.set(false);
        this.userSelectedType.set(null);
      }
    });

    // Effect 1: Update selector control when active schema changes
    effect(() => {
      const selectorValue = this.previousSelectedValue();
      if (selectorValue !== this.schemaSelectorControl.value) {
        this.schemaSelectorControl.setValue(selectorValue, { emitEvent: false });
      }
    });
  }

  /**
   * Called when user selects a new value in the schema selector dropdown.
   * Shows confirmation dialog if there would be data loss.
   */
  async onSchemaSelectionChange(event: MatSelectChange<string>): Promise<void> {
    const selectedValue = event.value;
    const prevValue = this.previousSelectedValue();

    if (selectedValue === prevValue) {
      return;
    }

    // Revert selector to previous value until confirmation
    this.schemaSelectorControl.setValue(prevValue ?? null, { emitEvent: false });

    try {
      const shouldProceed = await this.checkForDataLossAndConfirm(selectedValue);

      if (shouldProceed) {
        this.userSelectedType.set(selectedValue);
        this.isUserDriven.set(true);
      }
    } catch (error) {
      console.error('Error during schema change:', error);
    }
  }

  /**
   * Filters patch data to only include fields that exist in the target schema.
   * Always preserves 'id' and 'type' fields.
   * @param patch The patch data to filter
   * @param schema The target schema to validate against
   * @returns Filtered patch containing only valid fields
   */
  private filterPatchForSchema(
    patch: object | null,
    schema: SfoUiJSONSchema7 | null,
  ): object | null {
    if (!patch || !schema || typeof patch !== 'object') {
      return patch;
    }

    const schemaProperties = Object.keys(schema.properties || {});
    const filteredPatch: Record<string, unknown> = {};

    Object.entries(patch).forEach(([key, value]) => {
      // Always preserve id and type fields
      if (key === 'id' || key === 'type') {
        filteredPatch[key] = value;
        return;
      }

      // Include field if it exists in the schema
      if (schemaProperties.includes(key)) {
        filteredPatch[key] = value;
      }
    });

    return filteredPatch;
  }

  /**
   * Checks if changing to the target schema would cause data loss and shows confirmation dialog if needed.
   * @param targetSchemaType The schema type we're switching to
   * @returns Promise<boolean> true if should proceed, false if user cancelled
   */
  private async checkForDataLossAndConfirm(targetSchemaType: string): Promise<boolean> {
    // Find the target schema
    const schemas = this.cslSchemas();
    const targetSchema = schemas.find((schema) => {
      return schema.properties?.type?.const === targetSchemaType;
    });

    if (!targetSchema) {
      return false;
    }

    // Get current form value from patch
    const currentPatch = this.patchForm();
    if (!currentPatch || !Object.keys(currentPatch).length) {
      return true;
    }

    const { lostFields, fieldValues } = FieldComparisonHelper.getFieldsToLose(
      currentPatch,
      targetSchema,
    );

    if (lostFields.length === 0) {
      return true;
    }

    const dialogData: SchemaChangeDialogData = { lostFields, fieldValues };
    const dialogRef = this.dialog.open(SchemaChangeConfirmationDialogComponent, {
      data: dialogData,
      disableClose: true,
      width: '500px',
    });

    const shouldProceed = await firstValueFrom(dialogRef.afterClosed());

    return !!shouldProceed;
  }

  /**
   * Propagates form value events from the embedded dynamic-form.
   */
  onFormValue(value: object): void {
    this.formValue.emit(value);
  }

  /**
   * Propagates patch error events from the embedded dynamic-form.
   */
  onPatchError(error: unknown | unknown[]): void {
    this.patchError.emit(error);
  }
}
